import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, Lock, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

const AudioTrackPlayer = ({ track, index, onComplete, completed, earlyCompleteAt }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    let firedThreshold = false;
    const onTime = () => {
      setCurrent(a.currentTime);
      // Only fire an early completion if this track opts in via
      // earlyCompleteAt (e.g. the final track uses 0.9 so users who stop just
      // before the natural end still unlock the next step).
      if (
        !firedThreshold &&
        typeof earlyCompleteAt === "number" &&
        a.duration > 0 &&
        a.currentTime / a.duration >= earlyCompleteAt &&
        onComplete
      ) {
        firedThreshold = true;
        onComplete(index);
      }
    };
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      if (!firedThreshold && onComplete) {
        firedThreshold = true;
        onComplete(index);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [onComplete, index, earlyCompleteAt]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  const onSeek = (v) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = v[0];
    setCurrent(v[0]);
  };

  const onVol = (v) => {
    const a = audioRef.current;
    setVolume(v[0]);
    if (a) a.volume = v[0];
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-6 md:p-7 flex flex-col gap-5"
      data-testid={`audio-track-${index}`}
    >
      <audio ref={audioRef} src={track.src} preload="metadata" />

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="h-12 w-12 shrink-0 rounded-full bg-primary text-primary-foreground grid place-items-center btn-lift focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid={`audio-play-btn-${index}`}
        >
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-serif text-lg md:text-xl leading-tight text-foreground truncate">
              {track.title}
            </p>
            {completed && (
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-turquoise/20 text-brand-turquoise shrink-0"
                aria-label="Completed"
                data-testid={`audio-completed-${index}`}
              >
                <Check className="h-3 w-3" />
              </span>
            )}
          </div>
          {track.subtitle && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">{track.subtitle}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1 tabular-nums">
            {fmt(current)} <span className="mx-1 opacity-50">/</span> {fmt(duration)}
          </p>
        </div>
      </div>

      <Slider
        value={[Math.min(current, duration || 0)]}
        max={duration || 1}
        step={0.1}
        onValueChange={onSeek}
        className="w-full"
        data-testid={`audio-seek-${index}`}
      />

      <div className="flex items-center gap-3 text-muted-foreground">
        <Volume2 className="h-4 w-4" />
        <Slider
          value={[volume]}
          min={0}
          max={1}
          step={0.01}
          onValueChange={onVol}
          className="w-32"
          data-testid={`audio-volume-${index}`}
        />
      </div>
    </div>
  );
};

export const AudioTrack = ({ track, index, locked = false, completed = false, onComplete, earlyCompleteAt }) => {
  // Locked state — user hasn't finished the previous track yet.
  if (locked) {
    return (
      <div
        className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 md:p-7 flex items-center gap-4 min-h-[172px]"
        data-testid={`audio-track-${index}`}
      >
        <div className="h-12 w-12 shrink-0 rounded-full bg-muted grid place-items-center text-muted-foreground">
          <Lock className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="font-serif text-lg md:text-xl leading-tight text-foreground/80 truncate">
            {track.title}
          </p>
          {track.subtitle && (
            <p className="text-sm text-foreground/65 truncate mt-0.5">{track.subtitle}</p>
          )}
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1.5">
            Finish the previous conversation to unlock
          </p>
        </div>
      </div>
    );
  }

  if (!track.src) {
    return (
      <div
        className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 md:p-7 flex items-center gap-4 min-h-[172px]"
        data-testid={`audio-track-${index}`}
      >
        <div className="h-12 w-12 shrink-0 rounded-full bg-muted grid place-items-center text-muted-foreground">
          <Play className="h-5 w-5 ml-0.5 opacity-40" />
        </div>
        <div className="min-w-0">
          <p className="font-serif text-xl leading-tight text-foreground/70 truncate">{track.title}</p>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
            {track.note || "Coming soon"}
          </p>
        </div>
      </div>
    );
  }
  return <AudioTrackPlayer track={track} index={index} onComplete={onComplete} completed={completed} earlyCompleteAt={earlyCompleteAt} />;
};
